import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";

const COLORS = {
  color: "#4F46E5", // Accent Text
  hover: "#60A5FA", // Hover
  textPrimary: "#0A0F2C", // Primary Text
  textSecondary: "#475569", // Secondary Text
  links: "#1E3A8A", // Links
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [loading, setLoading] = useState(false);

  // Phone Login States
  const [loginMethod, setLoginMethod] = useState("email"); // 'email' or 'phone'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);

  const { login, googleLogin, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isRecruiterLogin = location.pathname === "/recruiter-login";
  const heading = isRecruiterLogin ? "Login to Hire Smarter" : "Welcome Back, Candidate";

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setRoleError("");
    setLoading(true);

    try {
      const expectedRole = isRecruiterLogin ? "admin" : "candidate";

      if (loginMethod === "email") {
        // login() may return either a full user (is_2fa_verified=true)
        // or a {requiresEmailOtp, tempToken, maskedEmail} object (first login).
        const result = await login(email, password, expectedRole);

        if (result && result.requiresEmailOtp) {
          // Store temp state in sessionStorage for the OTP page
          sessionStorage.setItem("otp_temp_token",  result.tempToken);
          sessionStorage.setItem("otp_masked_email", result.maskedEmail || "");
          // Remember where to redirect after successful OTP verification
          const redirectPath = isRecruiterLogin ? "/admin/dashboard" : "/candidate/dashboard";
          sessionStorage.setItem("otp_return_path", redirectPath);
          navigate("/verify-email-otp");
          return;
        }

        // Normal login — result is the full user object
        const user = result;
        if (user.role === "super_admin") {
          navigate("/super-admin/dashboard");
        } else if (user.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/candidate/dashboard");
        }

      } else {
        // Phone OTP flow (unchanged)
        if (!otpSent) {
          setError("Please request OTP first");
          setLoading(false);
          return;
        }
        const user = await verifyOtp(phoneNumber, isRecruiterLogin ? "recruiter" : "candidate", otp);
        if (user.role === "super_admin") {
          navigate("/super-admin/dashboard");
        } else if (user.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/candidate/dashboard");
        }
      }

    } catch (err) {
      if (err.response?.data?.code === "ROLE_MISMATCH") {
        setRoleError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || "Failed to login");
      }
    } finally {
      setLoading(false);
    }
  }

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await sendOtp(phoneNumber, isRecruiterLogin ? "recruiter" : "candidate");
      setOtpSent(true);
      setTimer(30);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (roleError) setRoleError("");
    if (error) setError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (roleError) setRoleError("");
    if (error) setError("");
  };

  const handleGoogleCredential = async (credential) => {
    setError("");
    setRoleError("");
    setLoading(true);
    try {
      const role = isRecruiterLogin ? "recruiter" : "candidate";
      const result = await googleLogin(credential, role);

      if (result && result.requiresEmailOtp) {
        sessionStorage.setItem("otp_temp_token",   result.tempToken);
        sessionStorage.setItem("otp_masked_email",  result.maskedEmail || "");
        const redirectPath = isRecruiterLogin ? "/admin/dashboard" : "/candidate/dashboard";
        sessionStorage.setItem("otp_return_path", redirectPath);
        navigate("/verify-email-otp");
        return;
      }

      if (result && result.requiresRegistration) {
        // New recruiter: store Google data, redirect to company registration
        sessionStorage.setItem("google_onboarding_data", JSON.stringify(result.googleData));
        navigate("/register?googleOnboarding=1");
        return;
      }

      // Successful login
      const user = result;
      if (user.role === "super_admin")   navigate("/super-admin/dashboard");
      else if (user.role === "admin")    navigate("/admin/dashboard");
      else                               navigate("/candidate/dashboard");

    } catch (err) {
      if (err.response?.data?.code === "ROLE_MISMATCH") {
        setRoleError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || "Google Sign-In failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "16px",
            border: "1px solid rgba(79, 70, 229, 0.2)",
            boxShadow: "0 10px 40px rgba(79, 70, 229, 0.15), 0 20px 60px rgba(96, 165, 250, 0.12)",
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ color: "#0A0F2C", fontSize: { xs: "1.75rem", md: "2.125rem" } }}
          >
            {heading}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {!isRecruiterLogin ? (
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                Are you a Recruiter?{" "}
                <Link
                  to="/recruiter-login"
                  style={{ color: "#1E3A8A", textDecoration: "none", fontWeight: 600 }}
                >
                  Click here to login
                </Link>
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                Are you a Candidate?{" "}
                <Link
                  to="/login"
                  style={{ color: "#1E3A8A", textDecoration: "none", fontWeight: 600 }}
                >
                  Click here to login
                </Link>
              </Typography>
            )}

          {roleError && (
            <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
              {roleError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              fullWidth
              variant={loginMethod === 'email' ? 'contained' : 'outlined'}
              onClick={() => { setLoginMethod('email'); setError(""); }}
              startIcon={<EmailIcon />}
              sx={{ borderRadius: '8px' }}
            >
              Email
            </Button>
            <Button
              fullWidth
              variant={loginMethod === 'phone' ? 'contained' : 'outlined'}
              onClick={() => { setLoginMethod('phone'); setError(""); }}
              startIcon={<PhoneIphoneIcon />}
              sx={{ borderRadius: '8px' }}
            >
              Phone
            </Button>
          </Box>

          {loginMethod === 'email' ? (
              <>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onMouseDown={() => setShowPassword(true)}
                          onMouseUp={() => setShowPassword(false)}
                          onMouseLeave={() => setShowPassword(false)}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    placeholder="10-digit mobile number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhoneNumber(val);
                      setError("");
                    }}
                    required
                    disabled={otpSent}
                  />
                  {!otpSent && (
                    <Button 
                      variant="contained" 
                      onClick={handleSendOtp} 
                      disabled={loading || phoneNumber.length !== 10}
                      sx={{ height: '56px', minWidth: '100px' }}
                    >
                      Send OTP
                    </Button>
                  )}
                </Box>

                {otpSent && (
                  <>
                    <TextField
                      fullWidth
                      label="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(val);
                        setError("");
                      }}
                      margin="normal"
                      required
                      autoFocus
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {timer > 0 ? `Resend OTP in ${timer}s` : ""}
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={handleSendOtp} 
                        disabled={loading || timer > 0}
                        sx={{ color: '#4F46E5' }}
                      >
                        Resend OTP
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}

            {/* Forgot Password */}
            {loginMethod === 'email' && (
              <Box sx={{ textAlign: "right", mt: 1 }}>
                <Link
                  to="/forgot-password"
                  style={{ color: "#475569", fontSize: "0.9rem" }}
                >
                  Forgot password?
                </Link>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={
                loading ||
                (loginMethod === 'phone' && (!otpSent || otp.length !== 6))
              }
            >
              {loading ? "Please wait..." : (loginMethod === 'phone' && otpSent ? "Verify OTP & Login" : "Login")}
            </Button>
          </form>

          {/* ── Google Sign-In (only rendered when Client ID is configured) ── */}
          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  or continue with
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
              </Box>
              <GoogleSignInButton
                onCredential={handleGoogleCredential}
                disabled={loading}
                text="continue_with"
              />
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Don’t have an account?{" "}
              <Link to="/register" style={{ color: "#1E3A8A" }}>
                Register here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
